import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { InfoTooltipComponent } from '../info-tooltip.component';

/**
 * Shared label-row content for formly fields: renders inside the field's own
 * `<mat-label>` (there is no central Formly label wrapper in this codebase — see
 * CLAUDE.md "Adding Form Builder properties" for the analogous per-file-type
 * duplication pattern). Adds the Figma "Optioneel" secondary-label badge for
 * non-required fields (FIX-SPEC #20951/#20952) and folds in the existing
 * info-tooltip, so callers no longer render `app-info-tooltip` separately.
 *
 * Deliberately does NOT touch the required-asterisk logic: some field types build
 * their own literal '*' into `label` before passing it in here (see date.component.ts),
 * others rely on Material's own required marker via `[required]` on the control —
 * both patterns keep working unchanged, this component only adds the Optioneel badge.
 */
@Component({
  selector: 'app-field-label-row',
  imports: [InfoTooltipComponent],
  template: `
    <!-- One line on purpose: a line break before the star collapses to a space of its own ("Naam  *"). -->
    <span class="dd-field-label-text"
      >{{ label() }}@if (requiredStar()) {<span class="dd-required-star"> *</span>}</span
    >
    @if (!required()) {
      <!-- aria-hidden: the badge sits inside <mat-label>, so without it every optional field's
           accessible name became "<label> Optioneel" (caught by the KvK story, whose
           getByRole('textbox', { name: 'KVK-nummer' }) stopped matching). Assistive tech already
           gets the same information from the control's own aria-required="false" — this badge is
           the visual half of that, not extra content. -->
      <span class="dd-optional-label" aria-hidden="true">Optioneel</span>
    }
    @if (info()) {
      <app-info-tooltip [tooltip]="info()"></app-info-tooltip>
    }
  `,
  host: {
    class: 'dd-field-label-row',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldLabelRowComponent {
  readonly label = input<string>('');
  /** Defaults to true (required) so a caller that forgets to bind it never shows "Optioneel" by mistake. */
  readonly required = input(true);
  readonly info = input<string | undefined>(undefined);
  /** Draws the " *" itself, in a span a borderless Figma card can hide; for fields without Material's own marker. */
  readonly requiredStar = input(false);
}
